import { useMemo, useEffect, useState } from "react";
import * as Typo from "typo-js";
import { alternativeNormalize, useCustomSpellcheck } from "../../../services/editor-manipulation";
import { splitPhrases, latinPhrases, customWords } from "./lis-dictionary-addendum";
const CURRENTLY_SHOWING_OPTIONS = {
    MISSPELLINGS: 'MISSPELLINGS',
    PERSONAL_DICTIONARY: 'PERSONAL_DICTIONARY',
}

const SpellCheck = props => {

    const [dictionary, setDictionary] = useState(null);
    const [misspellings, setMisspellings] = useState(new Map());

    const [scan, toggleScan] = useState(false);

    const [personalDictionary, setPersonalDictionary] = useState([]);

    const [isNormalized, toggleNormalized] = useState(false);

    const [currentlyShowing, setCurrentlyShowing] = useState(null);

    const [isDone, toggleDone] = useState(false);

    const spellcheckTooltip = props.allowGoToMisspelling ? '' : 'Spellcheck must be run after making updates to see the changes or go to an instance.';

    const [areYouSure, toggleAreYouSure] = useState({ show: false, word: '', action: null });

    const misspellingEntries = useMemo(() => {

        const r = [];
        const bads = []
        misspellings.forEach((v, k) => {
            const bad = v.filter(w => w.startIndex < 0 || w.endIndex < 0);
            const goods = v.filter(w => w.startIndex >= 0 || w.endIndex >= 0);
            bad.forEach(w => {
                bads.push(w);
            })
            if (bads.length > 0) {
                console.log('Bad words:', bads);
            }
            r.push(<div className='misspelling' key={k}>
                <h6>{k} - {v.length}</h6>
                <ul>{goods.map((val, i) =>
                    <li><span>{(i + 1)} - </span>
                        <span title={spellcheckTooltip} className={"instance " + (props.allowGoToMisspelling && ' enabled')} onClick={() => selectText(val.parent, val.startIndex, val.endIndex, val.closestDiv, val.indexes)}                                        >
                            {val.context}
                        </span>
                    </li>)}
                </ul>
                <button className='non-editor-button button' onClick={() => addToDictionary(k)}>Add to Dictionary</button>
            </div>)
        })
        return r;
    }, [misspellings, props.allowGoToMisspelling]);

    useEffect(() => {
        const filesExt = window.env?.APP_ENV.toLowerCase() === 'production' ? '' : window.env?.APP_ENV.toLowerCase();
        const newDictionary = new Typo(`we hardcode the name in the node_modules now`, false, false, {
            dictionaryPath: `${window.env?.STORAGE_ACCOUNT_DOMAIN}/files${filesExt}`
        });
        // Add a custom word
        // newDictionary.dictionaryTable['2025'] = null;
        customWords.forEach(word => {
            newDictionary.dictionaryTable[word] = null;
        });

        const fromStorage = JSON.parse(localStorage.getItem('spellcheck') ?? '[]');
        setPersonalDictionary(fromStorage);
        fromStorage.forEach(word => {
            newDictionary.dictionaryTable[word] = null;
        });
        setDictionary(newDictionary);
    }, []);

    useEffect(() => {
        if (props.editorsInitialized && dictionary && scan) {
            toggleNormalized(false);
            fireNormalize();
            toggleScan(false);
            const fromStorage = JSON.parse(localStorage.getItem('spellcheck') ?? '[]');
            setPersonalDictionary(fromStorage);
        }
    }, [props.editorsInitialized, dictionary, scan]);

    useEffect(() => {
        if (isNormalized) {
            scanForMisspelledWords();
        }
    }, [isNormalized]);

    useEffect(() => {
        if (currentlyShowing === CURRENTLY_SHOWING_OPTIONS.MISSPELLINGS) {
            fireNormalize();
        } else {

        }
    }, [currentlyShowing]);

    const handleCheckSpelling = (e) => {
        e.preventDefault();
        props.toggleAllowGoToMisspelling(true);
        toggleDone(false);
        if (CURRENTLY_SHOWING_OPTIONS.MISSPELLINGS === currentlyShowing) {
            fireNormalize();
        } else {
            setCurrentlyShowing(CURRENTLY_SHOWING_OPTIONS.MISSPELLINGS)
        }
    }

    const scanForMisspelledWords = (e) => {
        const isStartOfSectionOrReferencingAnotherOrSubsection = (word) => // A2, S6, 2a, 5g
            (word.match(/[A-Z][0-9](\.|,)?/)?.length ?? word.match(/[0-9][a-z](\.|,)?/)?.length) > 0;

        const isRomanNumeralWithParenthesis = (word) => // (i), (vi), (iii)(a)
            word.match(/^\((?=[mdclxvi])m{0,4}(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})\)(\([a-z]\))?$/)?.length > 0;

        const isIRSCode = (word) => // 168(k), 168(l), 168(m), 1400L, and 1400N
            word.match(/\d+[A-Za-z]?(?:\([a-z]\))?/g);

        const isAbbreviatedSpecialSession = (word, i, words) => // Sp. Sess.
            0 < i && words[i - 1].original === 'Sp.' && word === 'Sess.';

        const validCharCode = (code) => {
            // 0-9
            return (code >= 48 && code <= 57)
                // A-Z
                || (code >= 65 && code <= 90)
                // a-z
                || (code >= 97 && code <= 122)
        }

        const cleanWord = (word) => {
            if (word.length === 0) {
                return word;
            }
            let newWord = word;
            if (validCharCode(newWord.charCodeAt(0))) {

            } else {
                newWord = newWord.substring(1);
                newWord = cleanWord(newWord);
            }
            if (validCharCode(newWord.charCodeAt(newWord.length - 1))) {

            } else {
                newWord = newWord.substring(0, newWord.length - 1);
                newWord = cleanWord(newWord);
            }
            return newWord;
        }

        const getContentInFirstSection = (node, hasBeenLdtitle, hasBeenPatron) => {
            const closestP = closestParentOfType('P', node);
            let isLdTitle = hasBeenLdtitle;
            let isPatron = hasBeenPatron;
            if (closestP.className.toLowerCase().indexOf('ldtitle') > -1) {
                isLdTitle = true;
            } else if (closestP.className.toLowerCase().indexOf('billpatron') > -1) {
                isPatron = true;
            }
            else if (closestP.className.toLowerCase().indexOf('enactstm') > -1 || closestP.className.toLowerCase().indexOf('enactcls') > -1) {
                return { getContent: true, hasBeenLdtitle: isLdTitle, hasBeenPatron: isPatron };
            }
            return { getContent: isLdTitle !== isPatron, hasBeenLdtitle: isLdTitle, hasBeenPatron: isPatron };
        }

        const getContainerDetails = (theP, on, i) => {
            let n = on;
            const walker = document.createTreeWalker(theP, NodeFilter.SHOW_TEXT);
            const textNodes = [];
            let node;
            let hasBeenLdtitle = false;
            let hasBeenPatron = false;
            let getContent = i > 0;
            while (node = walker.nextNode()) {
                if (i === 0) {
                    const getContentDetails = getContentInFirstSection(node, hasBeenLdtitle, hasBeenPatron);
                    getContent = getContentDetails.getContent;
                    hasBeenLdtitle = getContentDetails.hasBeenLdtitle;
                    hasBeenPatron = getContentDetails.hasBeenPatron;
                }
                // Resolutions ... non introduced ... ?
                if (getContent) {
                    textNodes.push(node);
                }
            }
            const details = [];
            textNodes.forEach(tn => {
                const split = tn.textContent.split(/\s+/);
                let str = '';

                for (let i = 0; i < split.length; i++) {
                    str += split[i];
                    const startIndex = str.lastIndexOf(split[i]);
                    let endIndex = -1;
                    if (startIndex >= 0) {
                        endIndex = startIndex + split[i].length;
                    }
                    else {
                        str = str.substring(0, str.length - split[i].length);
                    }
                    if (i !== split.length - 1) {
                        str += ' ';
                    }
                    if (endIndex === -1 && tn.textContent.includes('"Tetrahydrocannabinol')) {
                        //debugger
                    }
                    details.push({
                        original: split[i],
                        cutDown: split[i],
                        parent: tn,
                        startIndex,
                        endIndex,
                        n,
                        indexes: getLocation(tn, theP.parentElement)
                    })
                    n++;
                }
            })
            return { separateWords: details, n };
        }

        const checkLatinPhrase = (words) => {
            const returnWords = [];
            words.forEach((word) => {
                const foundPhrases = splitPhrases.map((phrase, k) => phrase[0] === word.cutDown.toLowerCase() ? k : -1).filter(k => k >= 0)
                    .map((k) => {
                        const startIndex = returnWords.length - splitPhrases[k].length + 1;
                        if (startIndex >= 0) {
                            const newPhrase = returnWords.slice(startIndex, returnWords.length).map(w => w.cutDown.toLowerCase());
                            const phraseString = newPhrase.join(' ') + ' ' + word.cutDown.toLowerCase();
                            const latinPhrase = latinPhrases[k].toLowerCase();
                            return {
                                match: phraseString === latinPhrase,
                                word: word.cutDown, phraseString,
                                latin: latinPhrases[k],
                                startIndex,
                                n: word.n
                            };
                        } else {
                            return {}
                        }
                    })
                const matched = foundPhrases.find(f => f.match);
                if (matched) {
                    returnWords.length = matched.startIndex;
                }
                else {
                    returnWords.push(word);
                }
            })
            return returnWords;
        }

        const getContext = (v, allWords) => {
            const prevIII = v.n - 3;
            const prevII = v.n - 2;
            const prevI = v.n - 1;
            const nextI = v.n + 1;
            const nextII = v.n + 2;
            const nextIII = v.n + 3;
            let preContext = '';
            let postContext = '';
            if (prevIII > -1) {
                preContext += allWords[prevIII].original + ' ';
            }
            if (prevII > -1) {
                preContext += allWords[prevII].original + ' ';
            }
            if (prevI > -1) {
                preContext += allWords[prevI].original + ' ';
            }
            if (nextI < allWords.length) {
                postContext += allWords[nextI].original + ' ';
            }
            if (nextII < allWords.length) {
                postContext += allWords[nextII].original;
            }
            if (nextIII < allWords.length) {
                postContext += ' ' + allWords[nextIII].original;
            }
            return <>{preContext}<span style={{ color: 'red' }}>{v.original} </span>{postContext}</>
        }

        const getLocation = (textNode, closestDiv) => {
            const indexes = [];
            let currentNode = textNode;
            let parentElement = textNode.parentElement;
            while (parentElement && currentNode !== closestDiv) {
                const childNodes = Array.from(parentElement.childNodes);
                const index = childNodes.indexOf(currentNode);
                indexes.push(index);
                currentNode = parentElement;
                parentElement = parentElement.parentElement;
            }
            return indexes.reverse();
        }

        const counts = new Map();
        const containerDiv = document.getElementById(props.editorDiv);
        const subDivs = Array.from(containerDiv.children);
        const subPs = subDivs.flatMap(sd => Array.from(sd.children));
        const allWords = [];
        let n = 0;
        for (let i = 0; i < subPs.length; i++) {
            const sp = subPs[i];
            let text = sp.innerText;
            const ldTitleStart = text.indexOf('A BILL');
            text = text.substring(ldTitleStart);
            const firstDashIndex = text.indexOf('—————');
            const enactStatementIndex = text.indexOf('Be it enacted by');
            text = text.substring(0, firstDashIndex) + text.substring(enactStatementIndex);
            let returnedDetails = getContainerDetails(sp, n, i);
            n = returnedDetails.n;
            let separateWords = returnedDetails.separateWords;
            allWords.push(...separateWords);
            let words = separateWords
                .filter(w => customWords.indexOf(w.original) < 0)
                .filter(w => !isStartOfSectionOrReferencingAnotherOrSubsection(w.original))
                .filter(w => !isRomanNumeralWithParenthesis(w.original))
                .filter(w => !isIRSCode(w.original))
                .filter((w, i, arr) => !isAbbreviatedSpecialSession(w.original, i, arr))
                .map(w => ({ ...w, cutDown: cleanWord(w.cutDown) }))
                .filter(w => w.cutDown.length === 0 ||
                    (w.cutDown.match(/^[0-9]+(\.[0-9]+)?\-[0-9]+(\.[0-9]+(\:[0-9]+)?)?$/g)?.length ?? 0) < 1)
                .filter(w => (w.cutDown.match(/^([0-9]+,)?[0-9]+(\.[0-9]+)?$/g)?.length ?? 0) < 1)
                .filter(w => (w.cutDown.match(/^[0-9]+\/[0-9]+?$/g)?.length ?? 0) < 1)
                .flatMap(w => w.cutDown.split('-').map(w2 => ({
                    ...w,
                    cutDown: w2,
                })))
                .filter(w => (w.cutDown.match(/^([0-9]+,)?[0-9]+(\.[0-9]+)?$/g)?.length ?? 0) < 1)
                .filter(w => (w.cutDown.match(/^([0-9]+,)*?[0-9]+(\.[0-9]+)?$/g)?.length ?? 0) < 1)
                .filter(w => (w.cutDown.match(/^[0-9]+\/[0-9]+?$/g)?.length ?? 0) < 1)
                .filter(w => w.cutDown.length > 0)
            words = checkLatinPhrase(words).filter(w => w.cutDown.length > 0)

            let misspelledWords = words.filter(word => {
                return !dictionary.check(word.cutDown);
            })
            if (useCustomSpellcheck) {
                const closestSpans = misspelledWords.map(m => closestParentOfType('SPAN', m.parent)).filter(s => s?.className.includes('spelling-error') > -1)
                if (closestSpans.length !== misspelledWords.length) {
                    const newRange = document.createRange();
                    const toSurround = misspelledWords[closestSpans.length];
                    newRange.setStart(toSurround.parent, toSurround.startIndex);
                    newRange.setEnd(toSurround.parent, toSurround.endIndex);
                    const span = document.createElement('span')
                    span.classList.add('spelling-error')
                    newRange.surroundContents(span);
                    i--;
                    continue;
                }
            }
            misspelledWords = misspelledWords.map(w => ({
                ...w,
                closestDiv: sp.parentElement,
                context: getContext(w, allWords),
            }))
            misspelledWords.forEach(word => {
                if (counts.has(word.cutDown)) {
                    counts.set(word.cutDown, counts.get(word.cutDown).concat(word));
                } else {
                    counts.set(word.cutDown, [word]);
                }
            });
        }
        setMisspellings(counts);
        toggleNormalized(false);
        toggleDone(true);
    }

    const selectText = (el, start, end, closestDiv, indexes) => {
        if (!props.allowGoToMisspelling) {
            return;
        }
        if (useCustomSpellcheck === false) {
            try { // This shouldn't break, but just in case.
                let startContainer = closestDiv;
                for (let i = 0; i < indexes.length; i++) {
                    startContainer = startContainer.childNodes[indexes[i]];
                }
                const sel = document.getSelection();
                const rng = document.createRange();
                rng.setStart(startContainer, start);
                rng.setEnd(startContainer, end);

                if (closestDiv) {
                    setTimeout(() => {
                        closestDiv.focus();
                    });
                }
                sel.removeAllRanges();
                sel.addRange(rng);
                setTimeout(() => {
                    const rangeRect = rng.getBoundingClientRect();
                    const newY = rangeRect.y + window.scrollY - 100;
                    window.scrollTo(rangeRect.x, newY);
                });
            } catch (error) {
                console.error('Error selecting text:', error);
            }
        } else {
            const rangeRect = el.parentElement.getBoundingClientRect();
            const newY = rangeRect.y + window.scrollY - 100;
            window.scrollTo(rangeRect.x, newY);
            const otherSelected = document.querySelectorAll('.selected');
            otherSelected.forEach(os => {
                os.classList.remove('selected');
            });
            el.parentElement.classList.add('selected');
        }
    }

    const addToDictionary = (word) => {
        toggleAreYouSure({ show: true, word, action: 'add' });
    }

    const remove = (word) => {
        toggleAreYouSure({ show: true, word, action: 'remove' });
    }

    const fireNormalize = () => {
        const containerDiv = document.getElementById(props.editorDiv);
        const subDivs = Array.from(containerDiv.children);
        const subPs = subDivs.flatMap(sd => Array.from(sd.children));
        subPs.forEach(sp => {
            alternativeNormalize(sp);
        });
        toggleNormalized(true);
    }

    const goToLine = () => {
        const lineNumber = prompt('Enter the line number to go to:', '')
        const number = parseInt(lineNumber, 10);
        let found = false;
        if (!isNaN(number) && number > 0) {
            const lineNumberHolder = document.getElementById('other-line-numbers');
            const walker = document.createTreeWalker(lineNumberHolder, NodeFilter.SHOW_TEXT);
            // debugger
            let node;
            while (node = walker.nextNode()) {
                const lineIndex = node.textContent.indexOf(number);
                if (lineIndex > -1) {
                    found = true;
                    const range = document.createRange();
                    range.setStart(node, lineIndex);
                    range.setEnd(node, lineIndex + ('' + lineNumber).length);
                    const sel = document.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                    node.parentElement.scrollIntoView();
                    break;
                }
            }
            if (!found) { // Assume that its at the end of a previous version, so go to the end of this one.
                window.scroll(0, document.body.scrollHeight)
            }
        }
    }

    const updateDictionary = (word) => {
        if (areYouSure.action === 'add') {
            setDictionary(prev => {
                prev.dictionaryTable[word] = null;
                const fromStorage = JSON.parse(localStorage.getItem('spellcheck') ?? '[]');
                fromStorage.push(word);
                localStorage.setItem('spellcheck', JSON.stringify(fromStorage));
                return prev;
            });
            toggleScan(true);
        } else {
            setDictionary(prev => {
                delete prev.dictionaryTable[word];
                return prev;
            });
            const fromStorage = JSON.parse(localStorage.getItem('spellcheck') ?? '[]');
            const newStorage = fromStorage.filter(w => w !== word);
            localStorage.setItem('spellcheck', JSON.stringify(newStorage));
            toggleScan(true);
        }
        toggleAreYouSure({ show: false, action: null, word: '' });
    }

    return <div className='side-panel' style={{ position: 'sticky', top: '215px' }}>
        {areYouSure.show && areYouSure.action && <div className="schedule-modal">
            <div className="schedule-modal-content">
                <p>{`You are about to ${areYouSure.action} ${areYouSure.word} ${areYouSure.action === 'add' ? 'to' : 'from'} your dictionany. Are you sure?`}</p>
                <button className="button secondary float-right" onClick={() => toggleAreYouSure({ show: false, action: null, word: '' })}>No</button>
                <button className="button primary float-right" onClick={() => updateDictionary(areYouSure.word)} style={{ marginRight: "15px" }}>Yes</button>
                <br />
            </div>
        </div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 1fr', gap: '10px' }}>
            <button className='non-editor-button button' onClick={(e) => handleCheckSpelling(e)} title={spellcheckTooltip}>Check Spelling {!props.allowGoToMisspelling ? '\u27f3' : '\u00a0\u00a0\u00a0\u00a0\u00a0'}</button>
            <button className='non-editor-button button' onClick={e => setCurrentlyShowing(CURRENTLY_SHOWING_OPTIONS.PERSONAL_DICTIONARY)}>See your Dictionary</button>
            <button className='non-editor-button button' onClick={(() => goToLine())}>To Line #</button>
        </div>
        <div style={{ overflowY: 'auto', maxHeight: 'calc(75vh - 100px)' }}>
            {currentlyShowing === CURRENTLY_SHOWING_OPTIONS.MISSPELLINGS ?
                (isDone ? (misspellings.size > 0 ?
                    misspellingEntries :
                    <div className='no-misspellings'>No misspellings found.</div>) :
                    <div className="center-spinner spinner" />
                )
                : currentlyShowing === CURRENTLY_SHOWING_OPTIONS.PERSONAL_DICTIONARY ?
                    <div className="dictionary">
                        {personalDictionary.length > 0 ? personalDictionary.map(w =>
                            <div className='entry' onClick={() => remove(w)}><div className='word'>{w} <span style={{ color: 'red' }}>X</span></div></div>
                        ) :
                            <div>Nothing in your personal dictionary.</div>
                        }
                    </div>
                    : <></>
            }
        </div>
    </div>
}

export default SpellCheck;

// Yay BDS
const closestParentOfType = (targetNodeName, currentElement) => {
    if (currentElement?.nodeName === targetNodeName) {
        return currentElement;
    } else if (currentElement?.parentElement) {
        return closestParentOfType(targetNodeName, currentElement.parentElement);
    }
    return null;
}
